/**
 * Aba Por anúncio — a 3ª tela do Mercado Turbo: um MLB por vez, com TODAS as campanhas dele lado
 * a lado, a recomendação da mais vantajosa e o campo de CMV editável ("eu posso inputar
 * manualmente"). Onde a M.C. não é confiável a linha sai em laranja e a recomendação diz por quê.
 */
import { useEffect, useMemo, useState } from 'react'
import { Autocomplete, Box, Button, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography, useTheme } from '@mui/material'
import {
  rotuloJanela, classeMc, mcCheioDe, mcDe, mcHoje, melhorDoAnuncio, selKey, type Anuncio, type Promocao } from './logic'
import { usePromo } from './ctx'
import { fetchExcecoes, fetchGrupos } from './promocoesService'
import { SeloGrupo } from './GruposRegra'
import { AcaoCell, AdCell, IncMark, LARANJA, McTip, P, Panel, Pill, StatusPill, SeloAcao, brl, ddmm, mono, num, pct, useCorMc } from './ui'

type Col = 'camp' | 'tipo' | 'ml' | 'seller' | 'total' | 'orig' | 'promo' | 'mc'
const COLS: Array<[Col, string, 'left' | 'right']> = [
  ['camp', 'Promoção', 'left'], ['tipo', 'Tipo', 'left'], ['ml', 'ML', 'right'], ['seller', 'Seller', 'right'], ['total', 'Total', 'right'],
  ['orig', 'Preço original', 'right'], ['promo', 'Preço promocional', 'right'], ['mc', 'M.C.', 'right'],
]

export function AbaAnuncio({ mlbSel, setMlbSel }: { mlbSel: string | null; setMlbSel: (m: string | null) => void }) {
  const ctx = usePromo()
  const { conta, base, alvo, cmvManual } = ctx
  const t = useTheme()
  const cor = useCorMc()
  const [sort, setSort] = useState<{ key: Col; dir: 1 | -1 }>({ key: 'mc', dir: -1 })
  const [cmvTxt, setCmvTxt] = useState<string | null>(null)

  const a: Anuncio | null = useMemo(() => (mlbSel ? conta.ads.find((x) => x.mlb === mlbSel) : null) ?? conta.ads[0] ?? null, [conta, mlbSel])
  // Selo 🎄 do grupo com regra própria (ela, 05/10/2026): a fila só traz `grupo` nas linhas com ação,
  // então a lista completa de MLBs vem do serviço (lida uma vez ao abrir a aba); a fila cobre o resto.
  const [mlbGrupo, setMlbGrupo] = useState<Map<string, string>>(new Map())
  const [emExcecao, setEmExcecao] = useState<Set<string>>(new Set())
  useEffect(() => {
    void fetchGrupos().then((r) => {
      const m = new Map<string, string>()
      // MLB em dois grupos: vale o de prazo mais longo (spec §3.3)
      for (const g of [...(r?.grupos ?? [])].sort((x, y) => y.ate.localeCompare(x.ate))) if (g.estado !== 'vencido') for (const x of g.mlbs) if (!m.has(x.mlb)) m.set(x.mlb, g.nome)
      setMlbGrupo(m)
    })
    // exceção vigente manda no anúncio (acima do grupo): com ela, o selo 🎄 some (onda final, 05/10/2026)
    void fetchExcecoes().then((d) => setEmExcecao(new Set((d?.excecoes ?? []).filter((x) => x.estado !== 'vencida').map((x) => x.mlb))))
  }, [])
  const grupoNome = useMemo(() => {
    if (!a || emExcecao.has(a.mlb)) return null
    const daLista = mlbGrupo.get(a.mlb)
    if (daLista) return daLista
    const d = ctx.fila?.contas?.[ctx.contaId]?.decisoes?.find((x) => x.mlb === a.mlb && x.grupo != null)
    return d?.grupo != null ? (ctx.fila?.grupos?.find((g) => g.id === d.grupo)?.nome ?? d.grupo) : null
  }, [a, mlbGrupo, emExcecao, ctx.fila, ctx.contaId])
  if (!a) return <Panel><P>Nenhum anúncio na base.</P></Panel>

  const mcCheio = mcCheioDe(a, base)
  const hoje = mcHoje(a, base, cmvManual)
  const best = melhorDoAnuncio(a, base, cmvManual)
  const cmvAtual = cmvManual.get(a.mlb)
  const cmvBase = a.cmv_ajustado != null && base !== 'real' ? a.cmv_ajustado : a.cmv

  const rows = a.promocoes.map((p) => ({ p, m: mcDe(a, p, base, cmvManual) }))
  const val = (r: { p: Promocao; m: { rs: number | null } }): string | number | null => {
    switch (sort.key) {
      case 'camp': return (r.p.name || r.p.type_label || '').toLowerCase()
      case 'tipo': return r.p.type_label
      case 'ml': return r.p.ml_pct
      case 'seller': return r.p.seller_pct
      case 'total': return r.p.desc_total_pct
      case 'orig': return r.p.original_price
      case 'promo': return r.p.preco_promo
      default: return r.m.rs
    }
  }
  rows.sort((x, y) => {
    const vx = val(x), vy = val(y)
    if (typeof vx === 'string' || typeof vy === 'string') return String(vx ?? '').localeCompare(String(vy ?? '')) * sort.dir
    return ((vx ?? -1e12) - (vy ?? -1e12)) * sort.dir
  })

  const reco = (() => {
    if (!best.vencedora) return { tone: 'alert' as const, txt: <><b>⚠ Sem recomendação possível.</b> Nenhuma promoção deste anúncio tem margem calculável — falta {cmvAtual == null && cmvBase == null ? 'o custo do produto' : 'comissão ou frete da API'}.{cmvAtual == null && cmvBase == null ? ' Informe o CMV no campo acima para a tela calcular.' : ''}</> }
    const { p, m } = best.vencedora
    if (!best.confiavel) return { tone: 'alert' as const, txt: <><b>⚠ A melhor aparente é "{p.name || p.type_label}"</b> — MC {brl(m.rs)} ({pct(m.pct)}), mas esse número está <b>otimista</b>: falta {m.faltas.join(', ')} na conta. Não trate como recomendação até o dado entrar.</> }
    return { tone: 'good' as const, txt: <><Box component="b" sx={{ color: 'success.main' }}>✓ Mais vantajosa: "{p.name || p.type_label}"</Box> ({p.type_label}) — preço {brl(p.preco_promo)}, MC <b>{brl(m.rs)}</b> ({pct(m.pct)}). Comparada entre as {best.nCompletas} promoções com margem completa deste anúncio{best.nIncompletas ? `; outras ${best.nIncompletas} ficaram de fora por dado faltando` : ''}.</> }
  })()

  const aplicarCmv = () => {
    if (cmvTxt == null) return
    const v = parseFloat(cmvTxt.replace(',', '.'))
    ctx.setCmv(a.mlb, Number.isNaN(v) || v <= 0 ? null : Math.round(v * 100) / 100)
    setCmvTxt(null)
  }

  return (
    <Stack spacing={2}>
      <Panel title="Filtrar promoções por anúncio">
        <Autocomplete
          options={conta.ads.map((x) => x.mlb)}
          value={a.mlb}
          onChange={(_, v) => { setMlbSel(v); setCmvTxt(null) }}
          renderInput={(params) => <TextField {...params} size="small" label="ID (MLB), SKU ou título do anúncio…" />}
          filterOptions={(_, state) => {
            const q = state.inputValue.trim().toLowerCase()
            const lista = q ? conta.ads.filter((x) => `${x.titulo} ${x.mlb} ${x.sku ?? ''}`.toLowerCase().includes(q)) : conta.ads
            return lista.slice(0, 300).map((x) => x.mlb)
          }}
          getOptionLabel={(mlb) => { const x = conta.ads.find((y) => y.mlb === mlb); return x ? `${x.mlb} — ${x.titulo}` : mlb }}
          sx={{ maxWidth: 720 }}
        />
        <P>Digite parte do MLB, do SKU ou do título para achar o anúncio. A tabela abaixo mostra <b>todas</b> as campanhas em que este anúncio aparece.</P>
      </Panel>

      <Panel>
        <Stack direction="row" spacing={1.75} alignItems="center" flexWrap="wrap" useFlexGap>
          <AdCell a={a} size={56} />
          {grupoNome ? <SeloGrupo nome={grupoNome} /> : null}
        </Stack>
        <Stack direction="row" spacing={1.75} flexWrap="wrap" useFlexGap sx={{ mt: 1.25, fontSize: 12.5, color: 'text.secondary', alignItems: 'center' }}>
          {!a.sku ? <Box component="span" sx={{ color: LARANJA }}>sem SKU no anúncio</Box> : null}
          {a.logistica_label ? <Pill label={a.logistica_label} /> : null}
          <span>Preço Original: <b>{brl(a.preco_atual)}</b></span>
          <McTip a={a} p={null}>
            <span>M.C. no preço cheio: {mcCheio.pct == null
              ? <><Box component="b" sx={{ color: LARANJA }}>não calculável</Box><IncMark faltas={mcCheio.faltas.length ? mcCheio.faltas : ['dado']} /></>
              : <><Box component="b" sx={{ color: cor(classeMc(mcCheio, alvo)) }}>{brl(mcCheio.rs)}</Box> <Box component="span" sx={{ color: cor(classeMc(mcCheio, alvo)) }}>({pct(mcCheio.pct)})</Box><IncMark faltas={mcCheio.faltas} /></>}</span>
          </McTip>
          {hoje ? (
            <McTip a={a} p={hoje.p}>
              <span>M.C. hoje ({hoje.p.name || hoje.p.type_label}, {brl(hoje.p.preco_promo)}): {hoje.m.rs == null
                ? <Box component="b" sx={{ color: LARANJA }}>não calculável</Box>
                : <><Box component="b" sx={{ color: cor(classeMc(hoje.m, alvo)) }}>{brl(hoje.m.rs)}</Box> <Box component="span" sx={{ color: cor(classeMc(hoje.m, alvo)) }}>({pct(hoje.m.pct)})</Box><IncMark faltas={hoje.m.faltas} />{hoje.p.rebate_ml_rs ? <Box component="span" sx={{ color: 'text.disabled' }}> · ML abate {brl(hoje.p.rebate_ml_rs)} da taxa</Box> : null}</>}</span>
            </McTip>
          ) : <Box component="span" sx={{ color: 'text.disabled' }}>sem promoção ativa — a margem de hoje é a do preço cheio</Box>}
          <span>{a.pedidos_30d} pedido(s) em 30d</span>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
          <TextField size="small" label="CMV/un" placeholder="informar" value={cmvTxt ?? (cmvAtual ?? cmvBase ?? '')}
            onChange={(e) => setCmvTxt(e.target.value)} onBlur={aplicarCmv} onKeyDown={(e) => { if (e.key === 'Enter') aplicarCmv() }}
            slotProps={{ htmlInput: { inputMode: 'decimal', style: { textAlign: 'right', width: 90 } } }}
            sx={{ '& fieldset': { borderColor: cmvAtual != null ? 'primary.main' : 'warning.main' } }} />
          <Typography sx={{ fontSize: 11.5, color: cmvAtual != null ? 'primary.main' : 'text.disabled', fontWeight: cmvAtual != null ? 600 : 400 }}>
            {cmvAtual != null ? 'informado por você — vale só nesta tela, não corrige a base' : cmvBase == null ? <Box component="span" sx={{ color: LARANJA }}>sem custo — informe para ver a margem</Box> : `${a.cmv_origem ?? ''}${a.cmv_ajuste_motivo && base !== 'real' ? ` · ajustado: ${a.cmv_ajuste_motivo}` : ''}`}
          </Typography>
          {cmvAtual != null ? <Button size="small" color="inherit" onClick={() => { ctx.setCmv(a.mlb, null); setCmvTxt(null) }}>desfazer</Button> : null}
        </Stack>

        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mt: 1.5, fontSize: 12, color: 'text.secondary' }}>
          <span><Box component="i" sx={{ display: 'inline-block', width: 11, height: 11, borderRadius: 0.75, bgcolor: 'success.main', mr: 0.75, verticalAlign: 'middle' }} />M.C. {alvo}% ou mais — dentro da sua régua</span>
          <span><Box component="i" sx={{ display: 'inline-block', width: 11, height: 11, borderRadius: 0.75, bgcolor: 'warning.main', mr: 0.75, verticalAlign: 'middle' }} />abaixo de {alvo}%</span>
          <span><Box component="i" sx={{ display: 'inline-block', width: 11, height: 11, borderRadius: 0.75, bgcolor: LARANJA, mr: 0.75, verticalAlign: 'middle' }} />⚠ M.C. incompleta (falta custo, imposto ou frete)</span>
          <span><Box component="i" sx={{ display: 'inline-block', width: 11, height: 11, borderRadius: 0.75, bgcolor: 'error.main', mr: 0.75, verticalAlign: 'middle' }} />margem negativa</span>
        </Stack>
      </Panel>

      <Panel tone={reco.tone}><Typography variant="body2" sx={{ lineHeight: 1.6 }}>{reco.txt}</Typography></Panel>

      <Box sx={{ overflowX: 'auto', border: 1, borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper' }}>
        <Table size="small" sx={{ minWidth: 1020, '& th': { fontSize: 11, textTransform: 'uppercase', letterSpacing: '.05em', color: 'text.disabled', whiteSpace: 'nowrap', bgcolor: 'action.hover', cursor: 'pointer', userSelect: 'none' }, '& td': { whiteSpace: 'nowrap', verticalAlign: 'middle' } }}>
          <TableHead>
            <TableRow>
              {COLS.map(([k, l, al]) => (
                <TableCell key={k} align={al} onClick={() => setSort({ key: k, dir: sort.key === k ? (sort.dir === 1 ? -1 : 1) : -1 })}>
                  {l} <Box component="span" sx={{ opacity: 0.5, fontSize: 10 }}>{sort.key === k ? (sort.dir < 0 ? '▼' : '▲') : '⇅'}</Box>
                </TableCell>
              ))}
              <TableCell>Ação <SeloAcao /></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length ? rows.map(({ p, m }) => {
              const k = selKey(a, p)
              const semSplit = p.split_origem === 'não informado pela API'
              const cel = (v: number | null, rs: number | null) => semSplit || v == null
                ? <Box component="span" sx={{ color: 'text.disabled' }}>– –</Box>
                : <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', ...num }}><Box component="span" sx={{ fontWeight: 600, fontSize: 13.5 }}>{pct(v)}</Box><Box component="span" sx={{ fontSize: 11.5, color: 'text.secondary' }}>{rs == null ? '—' : brl(rs)}</Box></Box>
              const per = p.janela ? `janela ${rotuloJanela(p.janela)}` : p.start_date && p.finish_date ? `${ddmm(p.start_date)} a ${ddmm(p.finish_date)}` : p.type === 'LIGHTNING' ? 'sem data na API' : 'Período indeterminado'
              const c = classeMc(m, alvo)
              return (
                <TableRow key={p.id || p.type} hover sx={{ bgcolor: m.faltas.length ? `${t.palette.warning.main}12` : undefined }}>
                  <TableCell sx={{ whiteSpace: 'normal !important' }}>
                    <b>{p.name || p.type_label}</b>
                    <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>{per}</Typography>
                    <StatusPill p={p} k={k} />
                  </TableCell>
                  <TableCell>{p.type_label}</TableCell>
                  <TableCell align="right">{cel(p.ml_pct, p.ml_rs)}</TableCell>
                  <TableCell align="right">{cel(p.seller_pct, p.seller_rs)}</TableCell>
                  <TableCell align="right">{cel(p.desc_total_pct, p.desc_total_rs)}</TableCell>
                  <TableCell align="right" sx={num}>{brl(p.original_price ?? a.preco_atual)}</TableCell>
                  <TableCell align="right" sx={num}>{p.preco_promo == null ? <Box component="span" sx={{ color: 'text.disabled', fontStyle: 'italic', fontSize: 12.5 }}>Configure</Box> : brl(p.preco_promo)}</TableCell>
                  <TableCell align="right">
                    <McTip a={a} p={p}>
                      {m.rs == null ? <Box component="span" sx={{ color: 'text.disabled' }}>– –</Box> : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', ...num }}>
                          <Box component="span" sx={{ fontWeight: 600, fontSize: 13.5, color: cor(c) }}>{brl(m.rs)}</Box>
                          <Box component="span" sx={{ fontSize: 11.5, color: cor(c) }}>{pct(m.pct)}<IncMark faltas={m.faltas} /></Box>
                          {m.faltas.length ? <Box component="span" sx={{ fontSize: 10.5, color: LARANJA }}>falta {m.faltas.join(', ')}</Box> : null}
                        </Box>
                      )}
                    </McTip>
                  </TableCell>
                  <TableCell><AcaoCell a={a} p={p} icone /></TableCell>
                </TableRow>
              )
            }) : <TableRow><TableCell colSpan={9} sx={{ color: 'text.disabled', p: 1.5 }}>Este anúncio não tem promoção nenhuma na coleta.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Box>
      <Typography sx={{ ...mono, fontSize: 11, color: 'text.disabled' }}>{a.mlb} · {a.category_id}{a.date_created ? ` · criado em ${a.date_created.slice(0, 10)}` : ''}</Typography>
    </Stack>
  )
}
